import {
  AccessTimeRounded as AccessTimeRoundedIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  NotInterested as NotInterestedIcon,
} from "@mui/icons-material";
import { Chip } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";
import { apiRequest } from "../../utils/apiRequest";
import { showToast } from "../../utils/toastOptions";
import DataTable from "./DataTable";

const CommitmentsTable = ({ projectId, companyId, refreshTrigger, title }) => {
  const [subcontractData, setSubcontractData] = React.useState([]);
  const [purchaseOrderData, setPurchaseOrderData] = React.useState([]);

  useEffect(() => {
    if (!projectId || !companyId) return;

    const fetchData = async () => {
      try {
        const [subcontractRes, purchaseOrderRes] = await Promise.all([
          apiRequest(
            `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${projectId}/subcontracts/`,
            { method: "GET" }
          ),
          apiRequest(
            `${process.env.REACT_APP_API_BASE_URL}/commitments/companies/${companyId}/projects/${projectId}/purchase_orders/`,
            { method: "GET" }
          ),
        ]);

        setSubcontractData(Array.isArray(subcontractRes) ? subcontractRes : []);
        setPurchaseOrderData(Array.isArray(purchaseOrderRes) ? purchaseOrderRes : []);
      } catch (err) {
        showToast("error", err.message || "Failed to fetch commitments");
        setSubcontractData([]);
        setPurchaseOrderData([]);
      }
    };

    fetchData();
  }, [projectId, companyId, refreshTrigger]);

  const flattened = useMemo(() => {
    const combined = [];
    const subcontracts = Array.isArray(subcontractData) ? subcontractData : [];
    const purchaseOrders = Array.isArray(purchaseOrderData) ? purchaseOrderData : [];

    subcontracts.forEach((subcontract) => {
      combined.push({
        id: `subcontract-${subcontract.id}`,
        type: "Subcontract",
        contractNumber: subcontract.number || "N/A",
        contractCompany: subcontract.vendor?.company || "N/A",
        title: subcontract.title || "N/A",
        status: subcontract.status || "N/A",
        defaultRetainage:
          typeof subcontract.retainage_percent === "number"
            ? `${subcontract.retainage_percent}%`
            : "N/A",
        executed: subcontract.executed ? "Yes" : "No",
        startDate: subcontract.contract_start_date
          ? String(subcontract.contract_start_date)
          : "N/A",
        estimatedDate: subcontract.contract_estimated_completion_date
          ? String(subcontract.contract_estimated_completion_date)
          : "N/A",
        actualDate: subcontract.actual_completion_date
          ? String(subcontract.actual_completion_date)
          : "N/A",
        contractDate: subcontract.contract_date ? String(subcontract.contract_date) : "N/A",
        receivedDate: subcontract.signed_contract_received_date
          ? String(subcontract.signed_contract_received_date)
          : "N/A",
        issuedDate: subcontract.issued_on_date ? String(subcontract.issued_on_date) : "N/A",
        private: subcontract.private ? "Yes" : "No",
        allowNonAdmin: subcontract.show_line_items_to_non_admins ? "Yes" : "No",
        assignedTo: "N/A",
        deliveryDate: "N/A",
      });
    });

    purchaseOrders.forEach((purchaseOrder) => {
      combined.push({
        id: `purchase_order-${purchaseOrder.id}`,
        type: "Purchase Order",
        contractNumber: purchaseOrder.number || "N/A",
        contractCompany: purchaseOrder.vendor?.company || "N/A",
        title: purchaseOrder.title || "N/A",
        status: purchaseOrder.status || "N/A",
        defaultRetainage:
          typeof purchaseOrder.retainage_percent === "number"
            ? `${purchaseOrder.retainage_percent}%`
            : "N/A",
        executed: purchaseOrder.executed ? "Yes" : "No",
        startDate: "N/A",
        estimatedDate: "N/A",
        actualDate: "N/A",
        contractDate: purchaseOrder.contract_date ? String(purchaseOrder.contract_date) : "N/A",
        receivedDate: purchaseOrder.signed_contract_received_date
          ? String(purchaseOrder.signed_contract_received_date)
          : "N/A",
        issuedDate: purchaseOrder.issued_on_date ? String(purchaseOrder.issued_on_date) : "N/A",
        private: purchaseOrder.private ? "Yes" : "No",
        allowNonAdmin: purchaseOrder.show_line_items_to_non_admins ? "Yes" : "No",
        assignedTo:
          purchaseOrder.assignee?.name || purchaseOrder.assignee?.id
            ? `ID: ${purchaseOrder.assignee.id}`
            : "N/A",
        deliveryDate: purchaseOrder.delivery_date ? String(purchaseOrder.delivery_date) : "N/A",
      });
    });

    return combined;
  }, [subcontractData, purchaseOrderData]);

  const getStatusChip = (status = "N/A") => {
    const statusValue = status;

    if (statusValue === "Approved" || statusValue === "Received") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#2e7d32",
            },
          }}
          icon={<CheckCircleIcon fontSize="small" />}
        />
      );
    } else if (
      statusValue === "Processing" ||
      statusValue === "Submitted" ||
      statusValue === "Partially Received" ||
      statusValue === "Out For Bid" ||
      statusValue === "Out For Signature"
    ) {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#fff3cd",
            color: "#d84315",
            border: "1px solid #d84315",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#d84315",
            },
          }}
          icon={<AccessTimeRoundedIcon fontSize="small" />}
        />
      );
    } else if (statusValue === "Terminated" || statusValue === "Void") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            border: "1px solid #c62828",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#c62828",
            },
          }}
          icon={<NotInterestedIcon fontSize="small" />}
        />
      );
    } else if (statusValue === "Complete" || statusValue === "Draft") {
      return (
        <Chip
          label={statusValue}
          size="small"
          sx={{
            backgroundColor: "#f5f5f5",
            color: "#616161",
            border: "1px solid #616161",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
            "& .MuiChip-icon": {
              color: "#616161",
            },
          }}
          icon={
            statusValue === "Complete" ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <EditIcon fontSize="small" />
            )
          }
        />
      );
    } else {
      return (
        <Chip
          label={statusValue}
          size="small"
          color="default"
          sx={{
            display: "flex",
            alignItems: "center",
            lineHeight: 1.5,
          }}
        />
      );
    }
  };

  const columns = [
    { field: "type", headerName: "Type" },
    { field: "contractNumber", headerName: "Contract Number" },
    { field: "contractCompany", headerName: "Contract Company" },
    { field: "title", headerName: "Title" },
    {
      field: "status",
      headerName: "Status",
      Cell: (value) => getStatusChip(value),
    },
    { field: "defaultRetainage", headerName: "Default Retainage" },
    { field: "executed", headerName: "Executed" },
    { field: "assignedTo", headerName: "Assigned To" },
    { field: "startDate", headerName: "Start Date" },
    { field: "estimatedDate", headerName: "Estimated Completion Date" },
    { field: "actualDate", headerName: "Actual Completion Date" },
    { field: "contractDate", headerName: "Contract Date" },
    { field: "receivedDate", headerName: "Signed Contract Received Date" },
    { field: "deliveryDate", headerName: "Delivery Date" },
    { field: "issuedDate", headerName: "Issued On Date" },
    { field: "private", headerName: "Private" },
    { field: "allowNonAdmin", headerName: "Allow Non-Admin" },
  ];

  return (
    <DataTable
      data={flattened}
      columns={columns}
      noDataMessage="No Existing Commitments Found"
      title={title}
    />
  );
};

CommitmentsTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshTrigger: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
  title: PropTypes.string,
};

export default CommitmentsTable;
